import React from 'react';
import { Button, ButtonProps } from '@mui/material';
import { useTheme } from '../contexts/ThemeContext';

interface GenericButtonProps extends Omit<ButtonProps, 'variant'> {
    variant?: 'primary' | 'secondary' | 'outline' | 'text';
    children: React.ReactNode;
}

const GenericButton: React.FC<GenericButtonProps> = ({
    variant = 'primary',
    children,
    sx,
    ...props
}) => {
    const { theme } = useTheme();

    const getButtonStyles = () => {
        switch (variant) {
            case 'primary':
                return {
                    bgcolor: theme.palette.primary.main,
                    borderRadius: 3,
                    '&:hover': {
                        bgcolor: theme.palette.primary.dark,
                    },
                    '&:disabled': {
                        bgcolor: theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled,
                    }
                };
            case 'secondary':
                return {
                    bgcolor: theme.palette.secondary.main,
                    borderRadius: 3,
                    '&:hover': {
                        bgcolor: theme.palette.secondary.dark,
                    },
                    '&:disabled': {
                        bgcolor: theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled,
                    }
                };
            case 'outline':
                return {
                    color: theme.palette.text.secondary,
                    borderRadius: 3,
                    borderColor: theme.palette.divider,
                    '&:hover': {
                        borderColor: theme.palette.text.secondary,
                        bgcolor: theme.palette.action.hover,
                    }
                };
            case 'text':
                return {
                    color: theme.palette.text.secondary,
                    borderRadius: 3,
                    '&:hover': {
                        bgcolor: theme.palette.action.hover,
                    }
                };
            default:
                return {};
        }
    };

    const getButtonVariant = () => {
        switch (variant) {
            case 'primary':
            case 'secondary':
                return 'contained';
            case 'outline':
                return 'outlined';
            case 'text':
                return 'text';
            default:
                return 'contained';
        }
    };

    return (
        <Button
            variant={getButtonVariant()}
            sx={{
                ...getButtonStyles(),
                ...sx
            }}
            {...props}
        >
            {children}
        </Button>
    );
};

export default GenericButton;
